import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DiscordLive } from "@/components/DiscordLive";
import { toast } from "sonner";
import { MessagesSquare, ExternalLink, Save, Loader2, Trophy } from "lucide-react";

export default function Discord() {
  const { isAdmin } = useAuth();
  const RANK_LABELS = [["novice", "Novice"], ["chasseur", "Chasseur"], ["veteran", "Vétéran"], ["maitre", "Maître"], ["legende", "Légende"]];
  const [cfg, setCfg] = useState({ server_id: "", invite: "", webhook: "", restrict_guild: false, bot_token: "", rank_roles: {}, weekly_podium: false });
  const [botConfigured, setBotConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get("/discord").then(({ data }) => {
      setCfg({ server_id: data.server_id || "", invite: data.invite || "", webhook: data.webhook || "", restrict_guild: !!data.restrict_guild, bot_token: "", rank_roles: data.rank_roles || {}, weekly_podium: !!data.weekly_podium });
      setBotConfigured(!!data.bot_configured);
    }).catch(() => {});
  }, []);

  const setRole = (key, val) => setCfg((c) => ({ ...c, rank_roles: { ...c.rank_roles, [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/discord", cfg);
      setBotConfigured(!!data.bot_configured);
      setCfg((c) => ({ ...c, bot_token: "" }));
      toast.success("Discord enregistré");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const postPodium = async () => {
    setPosting(true);
    try {
      await api.post("/discord/podium");
      toast.success("Podium posté sur Discord !");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec");
    } finally { setPosting(false); }
  };

  return (
    <div className="fade-up max-w-3xl space-y-6">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-primary/80">Communauté</div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
          <MessagesSquare className="w-7 h-7" style={{ color: "#5865F2" }} /> Serveur Discord
        </h1>
      </div>

      {cfg.server_id && <DiscordLive adminHint={isAdmin} />}

      {cfg.server_id ? (
        <div className="rounded-lg border border-border overflow-hidden" data-testid="discord-widget">
          <iframe
            title="Discord"
            src={`https://discord.com/widget?id=${encodeURIComponent(cfg.server_id)}&theme=dark`}
            width="100%" height="420" allowtransparency="true" frameBorder="0"
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
          />
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-8 text-center text-sm text-muted-foreground" data-testid="discord-empty">
          <MessagesSquare className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: "#5865F2" }} />
          Aucun serveur Discord lié pour l'instant.
          {isAdmin ? " Renseigne l'ID de ton serveur ci-dessous." : " Reviens plus tard, l'admin va le configurer."}
        </div>
      )}

      {cfg.invite && (
        <a href={cfg.invite} target="_blank" rel="noopener noreferrer" data-testid="discord-join"
          className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold text-white transition-colors"
          style={{ background: "#5865F2" }}>
          <ExternalLink className="w-4 h-4" /> Rejoindre le Discord
        </a>
      )}

      {isAdmin && (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5 space-y-4">
          <h3 className="font-head font-semibold">Configuration (admin)</h3>
          <p className="text-xs text-muted-foreground">
            Active le widget sur Discord : <span className="text-primary">Paramètres du serveur → Widget → Activer</span>, puis copie l'ID du serveur.
          </p>
          <div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">ID du serveur Discord</label>
            <input data-testid="discord-server-id" value={cfg.server_id} onChange={(e) => setCfg({ ...cfg, server_id: e.target.value })} placeholder="123456789012345678" className="cfg-input" />
          </div>
          <div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Lien d'invitation</label>
            <input data-testid="discord-invite" value={cfg.invite} onChange={(e) => setCfg({ ...cfg, invite: e.target.value })} placeholder="https://discord.gg/xxxx" className="cfg-input" />
          </div>
          <div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Webhook « Annonce Légende »</label>
            <input data-testid="discord-webhook" value={cfg.webhook} onChange={(e) => setCfg({ ...cfg, webhook: e.target.value })} placeholder="https://discord.com/api/webhooks/..." className="cfg-input" />
            <p className="text-[10px] text-muted-foreground mt-1 leading-tight">
              Crée un webhook sur ton salon Discord (Paramètres du salon → Intégrations → Webhooks). Un message sera posté quand un joueur vainc les 34 boss (rang Légende).
            </p>
          </div>
          <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-[hsl(var(--surface-hover))] transition-colors" data-testid="discord-restrict-label">
            <input type="checkbox" data-testid="discord-restrict" checked={cfg.restrict_guild} onChange={(e) => setCfg({ ...cfg, restrict_guild: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#5865F2] cursor-pointer" />
            <span>
              <span className="text-sm font-semibold block">Réserver l'accès aux membres du serveur</span>
              <span className="text-[10px] text-muted-foreground leading-tight block mt-0.5">
                Seuls les membres de ton serveur Discord (ID ci-dessus) pourront se connecter. Toi (admin) as toujours accès. Nécessite un ID de serveur valide.
              </span>
            </span>
          </label>
          <div className="border-t border-border pt-4">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/70 mb-2">Récompenses de rang (bot)</div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">
              Token du bot {botConfigured && <span style={{ color: "#22C55E" }}>· configuré ✓</span>}
            </label>
            <input data-testid="discord-bot-token" type="password" value={cfg.bot_token} onChange={(e) => setCfg({ ...cfg, bot_token: e.target.value })} placeholder={botConfigured ? "•••••••• (laisser vide pour garder)" : "Token du bot Discord"} className="cfg-input" />
            <p className="text-[10px] text-muted-foreground mt-2 mb-2 leading-tight">
              ID du rôle Discord à attribuer pour chaque rang (laisse vide pour ne rien attribuer). Le bot doit être sur le serveur avec « Gérer les rôles », son rôle placé <span className="text-primary">au-dessus</span> des rôles attribués.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {RANK_LABELS.map(([key, label]) => (
                <div key={key}>
                  <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground mb-1 block">{label}</label>
                  <input data-testid={`discord-role-${key}`} value={cfg.rank_roles[key] || ""} onChange={(e) => setRole(key, e.target.value)} placeholder="ID du rôle" className="cfg-input" />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/70 mb-2">Podium hebdomadaire</div>
            <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-[hsl(var(--surface-hover))] transition-colors">
              <input type="checkbox" data-testid="discord-weekly-podium" checked={cfg.weekly_podium} onChange={(e) => setCfg({ ...cfg, weekly_podium: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#5865F2] cursor-pointer" />
              <span>
                <span className="text-sm font-semibold block">Poster le top 3 chaque semaine</span>
                <span className="text-[10px] text-muted-foreground leading-tight block mt-0.5">Nécessite un webhook. Le classement est publié automatiquement tous les 7 jours dans ton salon.</span>
              </span>
            </label>
            <button data-testid="discord-podium-now" onClick={postPodium} disabled={posting}
              className="mt-2 inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold hover:bg-[hsl(var(--surface-hover))] transition-colors disabled:opacity-50">
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />} Poster le podium maintenant
            </button>
          </div>

          <button data-testid="discord-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/85 transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      )}
    </div>
  );
}
